import { Component, inject, Injector, Input, Optional } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RimsStepEditModalComponent } from '../rims-edit-panel/rims-step-edit-modal.component';
import { ConfirmationModalComponent } from '../../../../../components/confirmation-modal/confirmation-modal.component';
import { firstValueFrom } from 'rxjs';
import { RimsStepInfo } from '@app/models/rims.model';
import { IncidentDetailsPageStore } from '@app/pages/incidents/details-page/incident-details-page.store';
import { RimsService } from '@app/services/rims/rims.service';
import { createRimsStepFromSchema } from '@app/utils/rims/rims.utils';
import { FieldReferenceService } from '../../field-references.service';
import { TechnicalStepModel } from '@app/models/technical-step.model';

@Component({
  standalone: true,
  selector: 'app-rims-card-step',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './rims-step-card.component.html',
  styleUrl: './rims-step-card.component.scss',
})
export class RimsStepCardComponent {
  private readonly dialog = inject(MatDialog);

  @Input() public technicalStep?: TechnicalStepModel | null;
  @Input() public rimsStepInfo?: RimsStepInfo;

  constructor(
    private readonly injector: Injector,
    private readonly rimsService: RimsService,
    @Optional() protected readonly incidentsPageStore?: IncidentDetailsPageStore, // This is marked as Optional() as this component is also used to display the Rims structure in the RimsPage,
    @Optional() private readonly fieldReferenceService?: FieldReferenceService, // This is marked as Optional() as this component relies on the store
  ) {}

  protected async onRemoveClickedAsync(): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Warning',
        content: `Step with name "${this.rimsStepInfo?.stepName}" will be deleted.`,
      },
    });

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult == false) {
      return;
    }

    this.incidentsPageStore!.incident()!.technicalSteps = this.incidentsPageStore!.incident()!.technicalSteps?.filter(
      (technicalStep) => technicalStep.rimsStep.$type !== this.technicalStep?.rimsStep?.$type,
    );

    const stepId = this.rimsStepInfo?.stepId;
    const technicalStepId = this.technicalStep?.id;
    if (stepId && technicalStepId) {
      this.fieldReferenceService?.removeTechnicalStepFieldReference(stepId, technicalStepId);
    }
    this.incidentsPageStore!.isRimsEdited = true;
  }

  protected async onDetailsClickedAsync(): Promise<void> {
    const rimsStepSchema = await firstValueFrom(this.rimsService.getRimsStepSchema(this.rimsStepInfo!.stepId));

    this.dialog.open(RimsStepEditModalComponent, {
      width: '1200px',
      maxWidth: 'none',
      panelClass: 'rims-edit-panel',
      disableClose: true,
      // The data here is copied because we want to edit it directly and then (if the user clicks update) to align the changes with the store's data.
      data: {
        rimsStep: this.technicalStep?.rimsStep ?? createRimsStepFromSchema(rimsStepSchema, true),
        rimsStepSchema: rimsStepSchema,
      },
      injector: this.injector,
    });
  }
}
